import { BeatSequencer } from '@/story/sequencer';
import { DAWN_TRANSPORT_BEAT, DAWN_TRANSPORT_CUES, DAWN_TRANSPORT_FLAGS } from '@/story/act3/dawnTransport';
import {
  RACE_GATE_AT, VEHICLE_GATE, YARD_DAWN_HERD_ARROW, YARD_DAWN_JUNCTION_BOX, YARD_DAWN_SPAWN,
} from '@/world/loadingYard';
import type { PlanMark } from '@/ui/mentalMap';
import type { Framing } from '@/camera/framing';
import type { BeatPartCapabilities, BeatPartModule, BeatPartModuleDefinition } from '@/game/beatPartModule';

/** The plan's `dawn` stage ends two metres clear of the door she came out of. */
const CLEAR_OF_DOOR = 2;

/**
 * The close: her in the open gateway, the herd streaming past her west into
 * the field, and Holly holding the race gate behind — the three things beat 13
 * opens on, in one frame (the plan's handover contract). Shot from the
 * paddock's east side, high enough to see over the string, looking back into
 * the yard. The lens follows her rather than a fixed gateway point: input is
 * live through the hold, and a fixed aim would frame an empty gate the moment
 * she walked on. It leans toward the race gate (`CLOSE_LOOK_TOWARD_GATE`) so
 * Holly stays in the frame.
 */
const CLOSE_SHOT_FROM = { x: VEHICLE_GATE.centreX + 4.2, y: 2.9, z: VEHICLE_GATE.z + 7.8 } as const;
const CLOSE_SHOT_FOV = 50;
const CLOSE_LOOK_TOWARD_GATE = 0.3;

/**
 * The three marks on her yard sheet (the plan's table). Ids are the seeds of
 * their hand-drawn jitter, so they are stable forever; positions come from the
 * room's own constants, so moving the box moves the mark.
 */
export const DAWN_PLAN_IDS = { lights: 'dawn.plan.lights', gate: 'dawn.plan.gate', herd: 'dawn.plan.herd' } as const;

/** Beat 12 part 1: the dawn yard's story, its flags, her plan and its closing shot. */
function createDawnTransportPartModule(capabilities: BeatPartCapabilities): BeatPartModule {
  const story = new BeatSequencer(DAWN_TRANSPORT_BEAT);
  const fade = capabilities.createFade(DAWN_TRANSPORT_CUES);
  // Copied out of the borrowed tick record, which the host reuses (see
  // `BeatPartTick`); the render step reads it for the close's aim.
  const her: { x: number; y: number; z: number } = { x: YARD_DAWN_SPAWN.x, y: YARD_DAWN_SPAWN.y, z: YARD_DAWN_SPAWN.z };
  const shot: Framing = { position: { ...CLOSE_SHOT_FROM }, lookAt: { x: 0, y: 0, z: 0 }, fov: CLOSE_SHOT_FOV };
  let holding = false;
  let disposed = false;
  // Mirrored from the story flags each tick, so a raise between ticks draws
  // the sheet the simulation last saw rather than asking the host again.
  let lightsStruck = false;
  let gateStruck = false;
  let herdStruck = false;
  return {
    story,
    interactables: [],
    get fade(): number { return fade.fade; },
    get promptSuppressed(): boolean { return !disposed && (holding || fade.fade > 0.5); },
    fixedStep({ player, raiseFlag, hasFlag }): void {
      her.x = player.x;
      her.y = player.y;
      her.z = player.z;
      if (Math.hypot(player.x - YARD_DAWN_SPAWN.x, player.z - YARD_DAWN_SPAWN.z) >= CLEAR_OF_DOOR) {
        raiseFlag(DAWN_TRANSPORT_FLAGS.clearOfDoor);
      }
      lightsStruck = hasFlag(DAWN_TRANSPORT_FLAGS.lightsOut);
      gateStruck = hasFlag(DAWN_TRANSPORT_FLAGS.gateHeld);
      herdStruck = hasFlag(DAWN_TRANSPORT_FLAGS.herdAtGate);
    },
    handleCue(cue): void {
      if (cue.id === DAWN_TRANSPORT_CUES.hold) holding = true;
      fade.handle(cue);
    },
    renderStep(frameDt): void { if (!disposed) fade.update(frameDt); },
    planMarks(): readonly PlanMark[] {
      return [
        { id: DAWN_PLAN_IDS.lights, glyph: 'box', at: [{ x: YARD_DAWN_JUNCTION_BOX.x, z: YARD_DAWN_JUNCTION_BOX.z + 0.4 }], struck: lightsStruck },
        { id: DAWN_PLAN_IDS.gate, glyph: 'gate', at: [{ x: RACE_GATE_AT.x, z: RACE_GATE_AT.z }], struck: gateStruck },
        { id: DAWN_PLAN_IDS.herd, glyph: 'arrow', at: YARD_DAWN_HERD_ARROW, struck: herdStruck },
      ];
    },
    framing(): Framing | null {
      if (!holding) return null;
      shot.lookAt.x = her.x + (RACE_GATE_AT.x - her.x) * CLOSE_LOOK_TOWARD_GATE;
      shot.lookAt.y = her.y + 0.2;
      shot.lookAt.z = her.z + (RACE_GATE_AT.z - her.z) * CLOSE_LOOK_TOWARD_GATE;
      return shot;
    },
    dispose(): void {
      if (disposed) return;
      disposed = true;
      fade.dispose();
    },
  };
}

export const DAWN_TRANSPORT_PART_MODULE: BeatPartModuleDefinition = {
  beat: 12,
  part: 1,
  room: 'yard-dawn',
  create: createDawnTransportPartModule,
};
